import { InferType, array, boolean, lazy, number, object, string } from "yup";
import { RDForT1Schema, SiteSchema } from "./Site";
import { UserSchema } from "./UserModel";
import { mapValues } from "lodash";
import { getKeywordFromString } from "../utils/keywordsHelper";
import moment from "moment";

export const PlanningMonths = [3, 6, 9, 12];

export const OtherAnchorType = [
    "en savoir plus",
    "voir ici",
    "ici",
    "plus d'infos ici",
    "plus d'informations",
    "ce site",
    "cet article",
    "ce blog",
    "ce lien",
    "cette url",
    "voir cette url",
    "voir ce lien",
    "voir ce blog",
    "voir cet article",
    "voir ce site",
    "consulter ce lien",
    "consulter cette url",
    "consulter ce blog",
    "consulter cet article",
    "consulter ce site",
];
export const MainAnchorType = ["www.", "https://", "https://www.", ""];

export const keywordsByDaySchema = object({ keyword: string(), date: string().nullable() });
export const keywordsByDayWithThematicSchema = object({
    keyword: string().required(),
    date: string()
        .notRequired()
        .transform((v1) => {
            if (!v1) return;
            return moment(v1.replace("  00:00:00", ""), "YYYY-MM-DD")
                .tz("Europe/Paris")
                .format("DD/MM/YYYY");
        }),
    thematicId: number(),
    link: object({ anchor: string(), url: string() }).notRequired(),
    rd: RDForT1Schema.notRequired().default(undefined),
});

export const OrderTypeSchema = object()
    .shape({
        filling: boolean().notRequired().default(null),
        ranking: boolean().notRequired().default(null),
        netlinking: boolean().notRequired().default(null),
        boost: boolean().notRequired().default(null),
        lead: boolean().notRequired().default(null),
        discover: boolean().notRequired().default(null),
        silo: boolean().notRequired().default(null),
    })
    .test({
        name: "orderType",
        message: "error.site.filling.required",
        test(values) {
            return !!values.netlinking || !!values.filling || !!values.ranking;
        },
    });

export const PlanningSchema = object({
    id: number().required(),
    moneySite: SiteSchema.required(),
    projectManager: UserSchema.required(),
    active: boolean(),
    months: number().required(),
    setAt: string()
        .optional()
        .transform((v1) => {
            if (!v1) return;
            return moment(v1).tz("Europe/Paris").format("YYYY-MM-DD");
        }),
    createdAt: string()
        .optional()
        .transform((v1) => {
            if (!v1) return;
            return moment(v1).tz("Europe/Paris").format("YYYY-MM-DD");
        }),
    orderType: OrderTypeSchema,
    price: number().nullable(),
    kwFilling: array()
        .of(
            object({
                keyword: string(),
                date: string()
                    .optional()
                    .transform((v1) => {
                        if (!v1) return;
                        return moment(v1.replace("  00:00:00", ""), "YYYY-MM-DD")
                            .tz("Europe/Paris")
                            .format("DD/MM/YYYY");
                    }),
            })
        )
        .nullable(),
    kwRanking: array()
        .of(
            object({
                keyword: string(),
                date: string()
                    .optional()
                    .transform((v1) => {
                        if (!v1) return;
                        return moment(v1.replace("  00:00:00", ""), "YYYY-MM-DD")
                            .tz("Europe/Paris")
                            .format("DD/MM/YYYY");
                    }),
            })
        )
        .notRequired(),
    kwDiscover: array()
        .of(
            object({
                keyword: string(),
                date: string()
                    .optional()
                    .transform((v1) => {
                        if (!v1) return;
                        return moment(v1.replace("  00:00:00", ""), "YYYY-MM-DD")
                            .tz("Europe/Paris")
                            .format("DD/MM/YYYY");
                    }),
            })
        )
        .notRequired(),
    kwLead: array()
        .of(
            object({
                keyword: string(),
                date: string()
                    .optional()
                    .transform((v1) => {
                        if (!v1) return;
                        return moment(v1.replace("  00:00:00", ""), "YYYY-MM-DD")
                            .tz("Europe/Paris")
                            .format("DD/MM/YYYY");
                    }),
            })
        )
        .notRequired(),
    kwBoost: array()
        .of(
            object({
                keyword: string(),
                date: string()
                    .optional()
                    .transform((v1) => {
                        if (!v1) return;
                        return moment(v1.replace("  00:00:00", ""), "YYYY-MM-DD")
                            .tz("Europe/Paris")
                            .format("DD/MM/YYYY");
                    }),
            })
        )
        .notRequired(),
    kwNetlinking: array().of(keywordsByDayWithThematicSchema).notRequired(),
});

export const UpsertPlanningSchema = PlanningSchema.pick([
    "months",
    "kwFilling",
    "orderType",
    "kwRanking",
    "kwBoost",
    "kwDiscover",
    "kwLead",
]).concat(
    object({
        moneySiteId: number(),
        projectManagerId: number(),
        kwNetlinking: array().of(keywordsByDayWithThematicSchema).notRequired(),
    })
);

export const UpsertNewPlanningSchema = object({
    id: number().notRequired(),
    months: number().required().default(PlanningMonths[0]),
    moneySiteId: number().notRequired(),
    projectManagerId: number().required(),
    price: number()
        .when("orderType.netlinking", {
            is: (value: boolean) => value,
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.notRequired(),
        })
        .default(30),
    orderType: OrderTypeSchema,
    fillingValue: number().when("orderType.filling", {
        is: (value: boolean) => value,
        then: (schema) => schema.min(1),
        otherwise: (schema) => schema.notRequired(),
    }),
    rankingValue: number().when("orderType.ranking", {
        is: (value: boolean) => value,
        then: (schema) => schema.min(1),
        otherwise: (schema) => schema.notRequired(),
    }),
    discoverValue: number().when("orderType.discover", {
        is: (value: boolean) => value,
        then: (schema) => schema.min(1),
        otherwise: (schema) => schema.notRequired(),
    }),
    leadValue: number().when("orderType.lead", {
        is: (value: boolean) => value,
        then: (schema) => schema.min(1),
        otherwise: (schema) => schema.notRequired(),
    }),
    boostValue: number().when("orderType.boost", {
        is: (value: boolean) => value,
        then: (schema) => schema.min(1),
        otherwise: (schema) => schema.notRequired(),
    }),
    netlinkingValue: number().when("orderType.netlinking", {
        is: (value: boolean) => value,
        then: (schema) => schema.min(1),
        otherwise: (schema) => schema.notRequired(),
    }),
});

export const UpsertRequestPlanningSchema = object({
    planningId: number().notRequired(),
});

export const UpsertFillingSchema = (maxValues: number) =>
    object().shape({
        kwFilling: string()
            .required()
            .default(undefined)
            .test({
                name: "upsertFilling",
                message: "error.site.ranking.min",
                test(value) {
                    return getKeywordFromString(value).length >= maxValues / 2;
                },
            }),
    });

export const UpsertRankingSchema = (maxValues: number) =>
    object().shape({
        kwRanking: string()
            .required()
            .default(undefined)
            .test({
                name: "upsertRanking",
                message: "error.site.ranking.min",
                test(value) {
                    return getKeywordFromString(value).length >= maxValues / 2;
                },
            }),
    });

export const UpsertLeadSchema = (maxValues: number) =>
    object().shape({
        kwLead: string()
            .required()
            .default(undefined)
            .test({
                name: "upsertLead",
                message: "error.site.lead.min",
                test(value) {
                    return getKeywordFromString(value).length >= maxValues / 2;
                },
            }),
    });

export const UpsertDiscoverSchema = (maxValues: number) =>
    object().shape({
        kwDiscover: string()
            .required()
            .default(undefined)
            .test({
                name: "upsertDiscover",
                message: "error.site.discover.min",
                test(value) {
                    return getKeywordFromString(value).length >= maxValues / 2;
                },
            }),
    });

export const UpsertBoostSchema = (maxValues: number) =>
    object().shape({
        kwBoost: string()
            .required()
            .default(undefined)
            .test({
                name: "upsertBoost",
                message: "error.site.boost.min",
                test(value) {
                    return getKeywordFromString(value).length >= maxValues / 2;
                },
            }),
    });

const lazySchema = lazy((obj) =>
    object(
        mapValues(obj, () => {
            return string().required().ensure();
        })
    )
);

export const UpsertPlanningKeywordDTO = object({
    kwFilling: string().required().ensure(),
    kwRanking: string().required().ensure(),
    kwLead: string().required().ensure(),
    kwBoost: string().required().ensure(),
    kwDiscover: string().required().ensure(),
    kwNetLinking: lazySchema,
});

export const UpsertNetlinkingSchema = object({ kwNetLinking: lazySchema });

export type Planning = InferType<typeof PlanningSchema>;
export type UpsertRequestPlanning = InferType<typeof UpsertRequestPlanningSchema>;
export type KeywordsByDay = InferType<typeof keywordsByDaySchema>;
export type KeywordsByDayWithThematic = InferType<typeof keywordsByDayWithThematicSchema>;
export type UpsertPlanningDTO = InferType<typeof UpsertPlanningSchema>;
export type UpsertNewPlanningDTO = InferType<typeof UpsertNewPlanningSchema>;
export type OrderType = InferType<typeof OrderTypeSchema>;
